import React, { useEffect, useState } from "react";
import { Button, Table, Pagination } from "react-bootstrap";
import { MdFilterAlt, MdFilterAltOff } from "react-icons/md";

const API_URL = process.env.REACT_APP_API_URL;

function CancelBookingList() {
  const [pendingBooking, setPendingBooking] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [showMessageModal, setShowMessageModal] = useState(false);

  const [searchTerm, setSearchTerm] = useState({
    project_name: "",
    block_name: "",
    plot_name: "",
    user_mobile: "",
  });

  const [messageModalContent, setMessageModalContent] = useState({
    title: "",
    text: "",
    type: "",
    confirmAction: null,
  });
  const [isFilterActive, setIsFilterActive] = useState(false);
  const handleToggle = () => setIsFilterActive(!isFilterActive);

  const showCustomMessageModal = (title, text, type, confirmAction = null) => {
    setMessageModalContent({ title, text, type, confirmAction });
    setShowMessageModal(true);
  };

  const closeCustomMessageModal = () => {
    setShowMessageModal(false);
    setMessageModalContent({
      title: "",
      text: "",
      type: "",
      confirmAction: null,
    });
  };

  const getAuthToken = () => localStorage.getItem("token");

  const fetchPendingBooking = async (page = 1, query = "") => {
    setLoading(true);
    setError(null);
    try {
      const token = getAuthToken();
      if (!token) {
        showCustomMessageModal(
          "Authentication Error",
          "Authentication token not found. Please log in.",
          "error",
        );
        return;
      }

      const queryParams = new URLSearchParams({
        page,
        project_name: searchTerm.project_name,
        block_name: searchTerm.block_name,
        plot_name: searchTerm.plot_name,
        user_mobile: searchTerm.user_mobile,
      }).toString();

      const url = new URL(`${API_URL}/cancel-booking-list?${queryParams}`);

      const response = await fetch(url.toString(), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          showCustomMessageModal(
            "Authorization Error",
            "Unauthorized: Please log in again.",
            "error",
          );
        }
        const errorData = await response.json();
        throw new Error(
          errorData.message || "Failed to fetch Cancel Booking.",
        );
      }

      const data = await response.json();
      setPendingBooking(data.data || []);
      setTotalPages(data.totalPages || 1);
      setCurrentPage(data.currentPage || page);
    } catch (err) {
      console.error("Fetch Cancel booking error:", err);
      setError(err.message);
      if (!showMessageModal) {
        showCustomMessageModal(
          "Error",
          err.message ||
            "An unexpected error occurred while fetching Cancel Booking.",
          "error",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingBooking(currentPage, searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, searchQuery]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPendingBooking(1, searchQuery);
  };

  // ---------------- LOADING ----------------
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // ---------------- ERROR ----------------
  if (error) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div className="alert alert-danger text-center" role="alert">
          {error}
          <button
            className="btn btn-primary ms-3"
            onClick={() => fetchPendingBooking()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ---------------- MAIN ----------------
  return (
    <>
      <div
        style={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f0f6ff 0%, #e6f0fa 100%)",
          padding: "30px 24px",
          fontFamily: "'Poppins', 'DM Sans', sans-serif",
        }}
      >
        <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
          {/* 🎯 HEADER CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              padding: "20px 24px",
              display: "flex",
              alignItems: "center",
              gap: "16px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                borderRadius: "14px",
                background: "linear-gradient(135deg, #dc2626, #ef4444)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "24px",
                boxShadow: "0 8px 20px rgba(220, 38, 38, 0.3)",
                flexShrink: 0,
              }}
            >
              ❌
            </div>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <h1
                style={{
                  margin: 0,
                  fontSize: "24px",
                  fontWeight: "800",
                  color: "#1e3a5f",
                  letterSpacing: "-0.5px",
                }}
              >
                Cancel Booking List
              </h1>
              <p
                style={{
                  margin: "2px 0 0",
                  fontSize: "13px",
                  color: "#64748b",
                  fontWeight: "500",
                }}
              >
                View all cancelled bookings
              </p>
            </div>

            <button
              className={`filter-toggle-btn btn ${
                isFilterActive ? "active" : ""
              }`}
              onClick={handleToggle}
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                border: "1.5px solid #e2e8f0",
                background: isFilterActive ? "#1e5799" : "#ffffff",
                color: isFilterActive ? "#ffffff" : "#1e3a5f",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                cursor: "pointer",
                transition: "all 0.25s ease",
                boxShadow: isFilterActive
                  ? "0 8px 20px rgba(30, 87, 153, 0.3)"
                  : "none",
              }}
              title={isFilterActive ? "Hide Filters" : "Show Filters"}
            >
              {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
            </button>
          </div>

          {/* 🎯 MAIN CARD */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(30, 87, 153, 0.08)",
              border: "1.5px solid #e2e8f0",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                height: "5px",
                background:
                  "linear-gradient(90deg, #1e5799, #2b6cb0, #1e5799)",
              }}
            />

            <div style={{ padding: "30px" }}>
              {/* FILTER PANEL */}
              {isFilterActive && (
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1.5px dashed #cbd5e1",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "24px",
                  }}
                >
                  <div className="d-flex gap-2 flex-wrap-mobile">
                    <div className="form_design w-100">
                      <input
                        type="text"
                        name="project_name"
                        placeholder="Project Name"
                        className="form-control"
                        value={searchTerm.project_name}
                        onChange={(e) =>
                          setSearchTerm({
                            ...searchTerm,
                            [e.target.name]: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="form_design w-100">
                      <input
                        type="text"
                        name="block_name"
                        placeholder="Block Name"
                        className="form-control"
                        value={searchTerm.block_name}
                        onChange={(e) =>
                          setSearchTerm({
                            ...searchTerm,
                            [e.target.name]: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="form_design w-100">
                      <input
                        type="text"
                        name="plot_name"
                        placeholder="Unit Number"
                        className="form-control"
                        value={searchTerm.plot_name}
                        onChange={(e) =>
                          setSearchTerm({
                            ...searchTerm,
                            [e.target.name]: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="form_design w-100">
                      <input
                        type="text"
                        name="user_mobile"
                        placeholder="Mobile"
                        className="form-control"
                        value={searchTerm.user_mobile}
                        onChange={(e) =>
                          setSearchTerm({
                            ...searchTerm,
                            [e.target.name]: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div className="form_design">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSearch}
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* TABLE */}
              <div className="table-responsive">
                <Table bordered hover>
                  <thead
                    style={{
                      background:
                        "linear-gradient(135deg, #1e5799, #2b6cb0)",
                      color: "#ffffff",
                    }}
                  >
                    <tr>
                      <th>#</th>
                      <th>Project Name</th>
                      <th>Block Name</th>
                      <th>Unit Number</th>
                      <th>User Name</th>
                      <th>User Mobile</th>
                      <th>Booking Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingBooking?.length > 0 ? (
                      pendingBooking.map((pendingbooked, i) => (
                        <tr key={pendingbooked.id}>
                          <td>{(currentPage - 1) * 10 + (i + 1)}</td>
                          <td>{pendingbooked.project_name}</td>
                          <td>{pendingbooked.block_name}</td>
                          <td>{pendingbooked.plot_name}</td>
                          <td>{pendingbooked.user_name}</td>
                          <td>{pendingbooked.user_mobile}</td>
                          <td>{pendingbooked.booking_date}</td>
                          <td>
                            <span
                              className="badge"
                              style={{
                                background:
                                  "linear-gradient(135deg, #dc2626, #ef4444)",
                                padding: "6px 12px",
                                fontSize: "12px",
                                fontWeight: "600",
                              }}
                            >
                              {pendingbooked.status}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="8"
                          className="text-center text-danger fw-bold"
                        >
                          No Cancel Booking found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              {/* PAGINATION */}
              <div className="d-flex justify-content-end mt-3">
                <Pagination>
                  <Pagination.Prev
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  {[...Array(totalPages)].map((_, index) => (
                    <Pagination.Item
                      key={index + 1}
                      active={index + 1 === currentPage}
                      onClick={() => handlePageChange(index + 1)}
                    >
                      {index + 1}
                    </Pagination.Item>
                  ))}
                  <Pagination.Next
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  />
                </Pagination>
              </div>
            </div>
          </div>

          {/* Footer Copyright */}
          <p
            style={{
              textAlign: "center",
              color: "#94a3b8",
              fontSize: "12.5px",
              marginTop: "24px",
              fontWeight: "500",
            }}
          >
            © {new Date().getFullYear()} A2 Demo Real Estates Pvt Ltd. All
            Rights Reserved.
          </p>
        </div>
      </div>

      {/* 🎯 MESSAGE MODAL */}
      {showMessageModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className={`modal-content ${
                messageModalContent.type === "success"
                  ? "border-success"
                  : messageModalContent.type === "error"
                    ? "border-danger"
                    : "border-warning"
              }`}
            >
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h5
                  className={`modal-title ${
                    messageModalContent.type === "success"
                      ? "text-success"
                      : messageModalContent.type === "error"
                        ? "text-danger"
                        : "text-warning"
                  }`}
                >
                  {messageModalContent.title}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={closeCustomMessageModal}
                ></button>
              </div>
              <div className="modal-body text-secondary">
                <p>{messageModalContent.text}</p>
              </div>
              <div className="modal-footer justify-content-center">
                {messageModalContent.confirmAction ? (
                  <>
                    <Button
                      variant="secondary"
                      onClick={closeCustomMessageModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant={
                        messageModalContent.type === "warning"
                          ? "warning"
                          : "primary"
                      }
                      onClick={() => {
                        messageModalContent.confirmAction();
                        closeCustomMessageModal();
                      }}
                    >
                      Confirm
                    </Button>
                  </>
                ) : (
                  <Button
                    variant={
                      messageModalContent.type === "success"
                        ? "success"
                        : messageModalContent.type === "error"
                          ? "danger"
                          : "primary"
                    }
                    onClick={closeCustomMessageModal}
                  >
                    OK
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default CancelBookingList;